import { useCallback, useEffect, useState } from "react";
import { AlertConfig } from "stores/data/insightAlerts/insightAlertModel";
import { useStores } from "stores/setup/use-store";

export function useAlertTemplates(templateType?: string) {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const [selectedTemplate, setSelectedTemplate] =
    useState<AlertConfig | null>(null);
  const [selectedTemplateType, setSelectedTemplateType] =
    useState<string | null>(null);

  const alertTemplates = insightAlertStore.alertTemplates;
  const macroRegionOrigin = insightAlertStore.macroRegionOrigin;
  const environmentalVariables = insightAlertStore.environmentalVariables;
  const selectedStation = insightAlertStore.selectedStationForAlert;

  const fetchLoadLayoutTemplates = useCallback(() => {
    if (alertTemplates.length === 0) {
      insightAlertStore.getAlertTemplateConfig();
    }
  }, [insightAlertStore, alertTemplates.length]);

  // Initial load of environmental variables (if not already loaded by store)
  const fetchLoadEnvironmentalVariables = useCallback(() => {
    if (macroRegionOrigin && environmentalVariables.length === 0) {
      // Build params based on current station selection
      const params: {
        macroRegionOrigin: string;
        stationId?: number;
        latitude?: number;
        longitude?: number;
        forceReload?: boolean;
      } = { macroRegionOrigin, forceReload: false };

      if (selectedStation) {
        if (selectedStation.station_id > 0) {
          params.stationId = selectedStation.station_id;
        } else if (selectedStation.station_id === 0) {
          params.latitude = selectedStation.station_lat;
          params.longitude = selectedStation.station_lon;
        }
      }

      insightAlertStore.getEnvironmentalVariables(params);
    }
  }, [insightAlertStore, macroRegionOrigin, environmentalVariables.length, selectedStation]);

  useEffect(() => {
    if (macroRegionOrigin && templateType) {
      const template = templateType;
      const templateConfig = insightAlertStore.getAlertTemplateByConfigId(
        `${template}_${macroRegionOrigin}`
      );
      setSelectedTemplate(templateConfig || null);
      setSelectedTemplateType(template);
    }
  }, [insightAlertStore, macroRegionOrigin, templateType]);

  useEffect(() => {
    fetchLoadLayoutTemplates();
  }, [fetchLoadLayoutTemplates]);

  // Initial load only - subsequent reloads are handled by the store
  // when setSelectedStationForAlert or setSelectedGeofence are called
  useEffect(() => {
    fetchLoadEnvironmentalVariables();
  }, [fetchLoadEnvironmentalVariables]);

  const resetTemplateSelection = useCallback(() => {
    setSelectedTemplate(null);
    setSelectedTemplateType(null);
  }, []);

  return {
    selectedTemplate,
    selectedTemplateType,
    resetTemplateSelection,
    environmentalVariables,
  };
}
